import AppConstants from "../constants/AppConstants.js";
import { LottieJsonSchema } from "../schemas/LottieSchemas.js";
import AssetUtils from "./AssetUtils.js";

const { LOTTIE_TYPE_ASSET, LOTTIE_TYPE_ASSET_LINK } = AppConstants;

const MIN_CANVAS_SIZE = 100;
const MAX_CANVAS_SIZE = 1920;
const DEFAULT_CANVAS_WIDTH = 400;
const DEFAULT_CANVAS_HEIGHT = 300;

/**
 * Validates whether a parsed JSON object is a valid Lottie animation.
 * @param {*} json - The parsed JSON object to validate
 * @returns {import("zod").SafeParseReturnType} Zod safe parse result with `success` boolean
 */
export const isValidLottieJson = (json) => LottieJsonSchema.safeParse(json);

/**
 * Validates a Lottie host URL and returns a detailed result with error type.
 * @param {string} urlValue - The URL to validate.
 * @returns {{ isValid: boolean, errorType: string|null }} Validation result with error type.
 */
export const validateLottieHostUrl = (urlValue) => {
  const trimmed = (urlValue || "").trim();
  if (!trimmed) return { isValid: true, errorType: null };
  try {
    const urlObj = new URL(trimmed);
    const isHttps = urlObj.protocol === "https:";
    const isLottieHost =
      urlObj.hostname === "lottie.host" ||
      urlObj.hostname.endsWith(".lottie.host");

    if (!isHttps) return { isValid: false, errorType: "protocol" };
    if (!isLottieHost) return { isValid: false, errorType: "domain" };
    return { isValid: true, errorType: null };
  } catch {
    return { isValid: false, errorType: "invalid" };
  }
};

/**
 * Validates that a URL belongs to the lottie.host domain and uses HTTPS.
 * @param {string} urlString - The URL to validate.
 * @returns {boolean} True if the URL is from lottie.host and uses HTTPS.
 */
export const isValidLottieHostUrl = (urlString) => {
  const trimmed = (urlString || "").trim();
  if (!trimmed) return false;
  return validateLottieHostUrl(trimmed).isValid;
};

/**
 * Resolves the animation data URL from Lottie content.
 * For asset types, resolves the key via AssetUtils. For asset links, returns the URL directly.
 * @param {Object} content - Immutable Map of content data.
 * @returns {string|null} The animation URL, or null if not resolvable.
 */
export const resolveAnimationUrl = (content) => {
  const type = content.get("type");
  if (type === LOTTIE_TYPE_ASSET) {
    const assetKey = content.get("key");
    return assetKey ? AssetUtils.getOriginalURL(assetKey) : null;
  }
  if (type === LOTTIE_TYPE_ASSET_LINK) {
    const url = content.get("url");
    return url ? url.trim() : null;
  }
  return null;
};


/**
 * Updates a Lottie canvas element's pixel buffer dimensions to match the given element size,
 * accounting for device pixel ratio. Dimensions are clamped between MIN_CANVAS_SIZE and MAX_CANVAS_SIZE.
 *
 * This is needed because DotLottie's built-in resize() uses `instanceof HTMLCanvasElement`
 * which fails for canvases created in iframe documents (cross-frame context).
 *
 * @param {HTMLCanvasElement} canvas - The canvas element to resize.
 * @param {number} width - The element width in CSS pixels.
 * @param {number} height - The element height in CSS pixels.
 * @param {Document} [ownerDoc] - The canvas's owner document (for accessing the correct devicePixelRatio).
 */
export const resizeLottieCanvas = (canvas, width, height, ownerDoc) => {
  const win = ownerDoc?.defaultView || window;
  const devicePixelRatio = win.devicePixelRatio || 1;
  const baseWidth = width > 0 ? width : DEFAULT_CANVAS_WIDTH;
  const baseHeight = height > 0 ? height : DEFAULT_CANVAS_HEIGHT;
  canvas.width = Math.max(
    MIN_CANVAS_SIZE,
    Math.min(Math.round(baseWidth * devicePixelRatio), MAX_CANVAS_SIZE)
  );
  canvas.height = Math.max(
    MIN_CANVAS_SIZE,
    Math.min(Math.round(baseHeight * devicePixelRatio), MAX_CANVAS_SIZE)
  );
};

/**
 * Creates a canvas element sized for a Lottie animation, accounting for device pixel ratio.
 * Canvas dimensions are clamped between MIN_CANVAS_SIZE and MAX_CANVAS_SIZE.
 * @param {string} elementId - The element ID (used for the canvas DOM id).
 * @param {number} elementWidth - The element width in CSS pixels.
 * @param {number} elementHeight - The element height in CSS pixels.
 * @param {Document} [ownerDoc=document] - The document to create the canvas in (use container.ownerDocument for iframe contexts).
 * @returns {HTMLCanvasElement} The configured canvas element.
 */

const LOTTIE_STYLE_ID = "lottie-canvas-styles";
const LOTTIE_CANVAS_CLASS = "lottie-canvas";

/**
 * injects a <style> tag with Lottie canvas styles into the given document's <head>.
 * Uses an id on the <style> element to avoid duplicate injection (one per iframe document).
 * @param {Document} doc - The document to inject styles into
 */
const ensureLottieStyles = (doc) => {
  if (doc.getElementById(LOTTIE_STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = LOTTIE_STYLE_ID;
  style.textContent = `.${LOTTIE_CANVAS_CLASS} { width: 100%; height: 100%; object-fit: contain; object-position: center; display: block; }`;
  doc.head.appendChild(style);
};

export const createLottieCanvas = (
  elementId,
  elementWidth,
  elementHeight,
  ownerDoc
) => {
  const doc = ownerDoc || document;
  const canvas = doc.createElement("canvas");
  canvas.id = `lottie-canvas-${elementId}`;
  resizeLottieCanvas(canvas, elementWidth, elementHeight, ownerDoc);
  ensureLottieStyles(doc);
  canvas.className = LOTTIE_CANVAS_CLASS;
  return canvas;
};
